import type { ReactElement } from "react";

import { Contact } from "@/pages/Contact";
import { ContactSent } from "@/pages/ContactSent";
import { ContactTrouble } from "@/pages/ContactTrouble";
import { Home } from "@/pages/Home";
import { NotFound } from "@/pages/NotFound";

/**
 * Every page on the site, and what goes in its <head>.
 *
 * One table, read by three things that must not disagree: the prerender
 * script writes an HTML file per entry, the client router picks a component
 * out of it, and the same script writes sitemap.xml from it. A route added
 * here is a route that exists everywhere; a route added anywhere else is a
 * route that half exists, which is the failure mode this table is here to
 * prevent.
 */
export type Route = {
  path: string;
  title: string;
  description: string;
  Component: () => ReactElement;
  /** Kept out of sitemap.xml — a confirmation page is not a landing page. */
  indexed: boolean;
};

export const ROUTES: Route[] = [
  {
    path: "/",
    title: "Keglam Services — Every business deserves a second opinion",
    description:
      "Independent, expert review of the technology your customers actually touch. Hire a programmer by the hour for security, SEO, and code quality checks — then pick up off-the-shelf tools built for your business.",
    Component: Home,
    indexed: true,
  },
  {
    path: "/contact",
    title: "Open a case file — Keglam Services",
    description:
      "Tell us what to review. One diagnostic hour at $125, no retainer and no lock-in, with a straight answer before you spend real money.",
    Component: Contact,
    indexed: true,
  },
  {
    path: "/contact/sent",
    title: "Enquiry received — Keglam Services",
    description: "Your enquiry reached us. We reply within one working day.",
    Component: ContactSent,
    indexed: false,
  },
  {
    path: "/contact/trouble",
    title: "Enquiry not sent — Keglam Services",
    description:
      "Something went wrong sending that enquiry. Email us directly instead.",
    Component: ContactTrouble,
    indexed: false,
  },
];

/**
 * Not in ROUTES because it is not a location — it is what the asset server
 * returns for anything that is not one. The prerender script writes it to
 * /404.html, which is what `not_found_handling` in wrangler.jsonc serves.
 */
export const NOT_FOUND: Omit<Route, "path" | "indexed"> = {
  title: "Page not found — Keglam Services",
  description: "That page is not here.",
  Component: NotFound,
};

export function routeFor(path: string): Omit<Route, "path" | "indexed"> {
  return ROUTES.find((route) => route.path === path) ?? NOT_FOUND;
}
